import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../redux/helpers/axios";

/**
 * @author
 * @function Discount
 **/

const BRAND = "#2F5651";

const emptyForm = {
  type: "PERCENTAGE",
  amount: "",
  currency: "BDT",
  code: "",
  isActive: true,
  startDate: "",
  endDate: "",
  instructions: [""],
  minimumOrderValue: "",
  maximumDiscountValue: "",
};

export const Discount = (props) => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const fetchDiscounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get("/discounts/services/get-all");
      const data = res.data;
      if (!data.success)
        throw new Error(data.msg || "Failed to load discounts");
      setDiscounts(data.discounts || data.discount || []);
    } catch (err) {
      setError(err.response?.data?.msg || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  const openModal = () => {
    setForm(emptyForm);
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setShowModal(false);
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateInstruction = (idx, value) => {
    setForm((prev) => {
      const next = [...prev.instructions];
      next[idx] = value;
      return { ...prev, instructions: next };
    });
  };

  const addInstruction = () => {
    setForm((prev) => ({ ...prev, instructions: [...prev.instructions, ""] }));
  };

  const removeInstruction = (idx) => {
    setForm((prev) => ({
      ...prev,
      instructions: prev.instructions.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    const cleanedInstructions = form.instructions
      .map((i) => i.trim())
      .filter(Boolean);

    if (!form.code.trim()) return setFormError("Discount code is required.");
    if (!form.amount || Number(form.amount) <= 0)
      return setFormError("Enter a valid amount.");
    if (!form.startDate || !form.endDate)
      return setFormError("Start and end dates are required.");
    if (new Date(form.startDate) > new Date(form.endDate))
      return setFormError("Start date must be before end date.");
    if (cleanedInstructions.length === 0)
      return setFormError("Add at least one instruction.");

    setSubmitting(true);
    try {
      const res = await axiosInstance.post("/discounts/services/create-new", {
        ...form,
        amount: Number(form.amount),
        instructions: cleanedInstructions,
      });
      const data = res.data;
      if (!data.success)
        throw new Error(data.msg || "Failed to create discount");

      setShowModal(false);
      fetchDiscounts();
    } catch (err) {
      setFormError(err.response?.data?.msg || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    const date = new Date(d);
    return isNaN(date) ? "-" : date.toLocaleDateString();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Discounts</h1>
        <button
          onClick={openModal}
          className="px-4 py-2 rounded-md text-white font-medium hover:opacity-90 transition"
          style={{ backgroundColor: "black" }}
        >
          + New Discount
        </button>
      </div>

      {loading && <p className="text-gray-500">Loading discounts...</p>}
      {error && (
        <p className="text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      {!loading && !error && discounts.length === 0 && (
        <div className="text-center py-16 border border-dashed border-gray-300 rounded-lg text-gray-500">
          No discounts yet. Create one to get started.
        </div>
      )}

      {!loading && !error && discounts.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Code
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Type
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Amount
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Status
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Minimum Order Value
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Maximum Discount Value
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Start
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  End
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {discounts.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {d.code}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{d.type}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {d.type === "PERCENTAGE"
                      ? `${d.amount}%`
                      : `${Number(d.amount).toFixed(2)} ${d.currency}`}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        d.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {d.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {d.minimumOrderValue
                      ? `${d?.minimumOrderValue} ${d.currency}`
                      : "-"}
                  </td>

                  <td className="px-4 py-3 text-gray-600">
                    {d.maximumDiscountValue
                      ? `${d?.maximumDiscountValue} ${d.currency}`
                      : "-"}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatDate(d.startDate)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatDate(d.endDate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                New Discount
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {formError && (
                <p className="text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm">
                  {formError}
                </p>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) => updateField("type", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                    style={{ "--tw-ring-color": BRAND }}
                  >
                    <option value="PERCENTAGE">Percentage</option>
                    <option value="FIXED">Fixed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <select
                    value={form.currency}
                    onChange={(e) => updateField("currency", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  >
                    <option value="BDT">BDT</option>
                    <option value="USD">USD</option>
                    <option value="CNY">CNY</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => updateField("amount", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code
                  </label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) =>
                      updateField("code", e.target.value.toUpperCase())
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                    placeholder="EID2026"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Order Value
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.minimumOrderValue}
                    onChange={(e) =>
                      updateField("minimumOrderValue", e.target.value)
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Discount Value
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.maximumDiscountValue}
                    onChange={(e) =>
                      updateField("maximumDiscountValue", e.target.value)
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => updateField("startDate", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => updateField("endDate", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => updateField("isActive", e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Active immediately
                </label>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Instructions
                  </label>
                  <button
                    type="button"
                    onClick={addInstruction}
                    className="text-sm font-medium"
                    style={{ color: BRAND }}
                  >
                    + Add
                  </button>
                </div>
                <div className="space-y-2">
                  {form.instructions.map((instruction, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        value={instruction}
                        onChange={(e) => updateInstruction(idx, e.target.value)}
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                        placeholder="e.g. Minimum order 2000 BDT"
                      />
                      {form.instructions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeInstruction(idx)}
                          className="px-2 text-gray-400 hover:text-red-500"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-md text-gray-700 border border-gray-300 hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md text-white font-medium hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: BRAND }}
                  disabled={submitting}
                >
                  {submitting ? "Creating..." : "Create Discount"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
